// Lightweight web client error reporter. Feeds client-side crashes into the
// shared backend's db.error_events (POST /api/telemetry/error), which surfaces in
// the Founder "System Health" view. Best-effort, deduped and session-capped so a
// crash loop can never flood the backend. Never throws.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/telemetry/error`;

let _installed = false;
let _count = 0;
const _seen = new Map(); // dedupe key -> last timestamp
const MAX_PER_SESSION = 50;
const DEDUPE_MS = 10_000;

export function reportClientError(payload = {}) {
  try {
    if (!BACKEND_URL || _count >= MAX_PER_SESSION) return;
    const path =
      payload.path ||
      (typeof window !== "undefined" ? window.location?.pathname : undefined);
    const key = `${payload.message || ""}|${payload.context || ""}|${path || ""}`;
    const now = Date.now();
    const last = _seen.get(key);
    if (last && now - last < DEDUPE_MS) return;
    _seen.set(key, now);
    _count += 1;

    const body = JSON.stringify({
      source: "web",
      platform: "web",
      level: payload.level || "error",
      name: payload.name,
      kind: payload.kind || payload.name,
      message: String(payload.message || "Client error").slice(0, 1000),
      stack: payload.stack ? String(payload.stack).slice(0, 4000) : undefined,
      path,
      context: payload.context,
    });

    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    /* reporting must never throw */
  }
}

export function installGlobalErrorReporters() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;

  window.addEventListener("error", (event) => {
    const err = event?.error;
    reportClientError({
      name: err?.name || "Error",
      message: err?.message || event?.message || "Uncaught error",
      stack: err?.stack,
      context: "window.onerror",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    reportClientError({
      name: reason?.name || "UnhandledRejection",
      message:
        reason?.message || String(reason ?? "") || "Unhandled promise rejection",
      stack: reason?.stack,
      context: "unhandledrejection",
    });
  });
}
